import React, {useCallback, useMemo} from "react";
import Chips, {ChipsTypes} from "../../../base/chips/chips";
import {useAppSelector} from "../../../../redux";
import Grid2 from "@mui/material/Unstable_Grid2";
import CustomerAutocomplete from "./index";

/**
 * Interface representing a customer option in the autocomplete component.
 * Used for displaying and managing customer selections.
 * @property id - Unique identifier for the customer (string format for UI)
 * @property name - Display name of the customer
 * @property city - City where the customer is located
 */
interface CustomerOption {
    id: string;
    name: string;
    city: string;
}

/**
 * Interface representing the customer information in the Redux state.
 * Note: This differs from CustomerOption as it uses number for ID and has optional fields.
 * @property id - Unique identifier for the customer (number format from backend)
 * @property name - Display name of the customer
 * @property city - Optional city where the customer is located
 */
interface CustomerInformationReduxState {
    id?: number;
    name: string;
    city?: string;
}

/**
 * MultiCustomerSearchSectionAutocomplete Component
 *
 * A component that provides a multi-select interface for customers with the following features:
 * - Displays selected customers as removable chips
 * - Provides an autocomplete search input for adding new customers
 * - Handles a default customer from Redux state
 * - Prevents duplicate customer selections
 * - Maintains order with default customer always first
 *
 * @component
 * @param {Object} props - Component props
 * @param {CustomerOption[]} props.customers - Array of currently selected customers
 * @param {Function} props.setCustomers - State setter function for updating the customers array
 */
const MultiCustomerSearchSectionAutocomplete: React.FC<{
    customers: CustomerOption[];
    setCustomers: React.Dispatch<React.SetStateAction<CustomerOption[]>>;
}> = ({
          customers,
          setCustomers,
      }) => {
    /**
     * Retrieves the default customer from Redux state.
     * This customer gets special treatment in the UI (always appears first if selected).
     */
    const defaultCustomer = useAppSelector(state => state.customerInformation) as CustomerInformationReduxState;

    /**
     * Memoized check to determine if the default customer is already in the selected list.
     * Prevents duplicate addition of the default customer.
     * Note: Converts ID types for comparison (number from Redux to string for UI).
     */
    const defaultCustomerSelected = useMemo(() =>
        !!defaultCustomer && customers.find(e => e.id === String(defaultCustomer.id)), [defaultCustomer, customers]);

    /**
     * Memoized list of all selected customers, including the default customer if applicable.
     * Handles the following cases:
     * - Maintains existing customer selections
     * - Adds default customer at the start if not already selected
     * - Handles optional fields and type conversions for the default customer
     */
    const selectedCustomers = useMemo(() => {
        const result = [...(customers ?? [])];
        if (!defaultCustomerSelected && !!defaultCustomer?.id) {
            result.unshift({
                id: String(defaultCustomer.id),
                name: defaultCustomer.name,
                city: defaultCustomer.city ?? '',
            });
        }
        return result;
    }, [customers, defaultCustomer, defaultCustomerSelected]);

    /**
     * Callback to remove a customer from the selection.
     * Filters out the customer with the matching ID from the current selection.
     *
     * @param customer - The CustomerOption object to remove
     */
    const removeCustomer = useCallback((customer: CustomerOption) => {
        setCustomers(prevState => prevState.filter(e => e.id !== customer.id));
    }, [setCustomers]);

    /**
     * Callback to add a new customer to the selection.
     * Handles several special cases:
     * - Prevents null customer additions
     * - Checks for and prevents duplicate selections
     * - Gives special treatment to the default customer (adds to start of list)
     * - Adds non-default customers to the end of the list
     *
     * @param customer - The CustomerOption object to add (or null if no selection)
     */
    const addCustomer = useCallback((customer: CustomerOption | null) => {
        if (!customer) return;
        setCustomers(prevState => {
            const alreadyExists = prevState.map(e => e.id).includes(customer.id);
            if (alreadyExists) {
                return prevState;
            }
            const addingDefault = defaultCustomer?.id === Number(customer.id);
            if (addingDefault) {
                return [customer, ...prevState];
            } else {
                return [...prevState, customer];
            }
        });
    }, [defaultCustomer?.id, setCustomers]);

    return (
        <>
            <Grid2 container spacing={2} alignItems={'center'}>
                {
                    selectedCustomers.map((customer) => (
                        <Grid2 xs={'auto'} key={customer.id}>
                            <Chips
                                type={ChipsTypes.remove}
                                content={customer.name}
                                onIconClick={() => removeCustomer(customer)}
                            />
                        </Grid2>
                    ))
                }
                {
                    <Grid2 xs={'auto'}>
                        <CustomerAutocomplete
                            value={null}
                            onChange={(_, customer) => addCustomer(customer)}
                            label={'Customer'}
                            placeholder={'Search for customer...'}
                            filterOptions={(options) => options.filter(option => !customers.some(e => e.id === option.id))}
                            sx={{minWidth: 'min(400px, 90vw)'}}
                            textFieldProps={{
                                variant: 'filled',
                                InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                            }}
                        />
                    </Grid2>
                }
            </Grid2>
        </>
    );
};

export default MultiCustomerSearchSectionAutocomplete; 